import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatNumber } from '../../../utils/reportHelpers';
import { Granularity, TimeSeriesPoint } from './types';

interface InvestigationsOverTimeChartProps {
  series: TimeSeriesPoint[];
  granularity: Granularity;
  isMobile: boolean;
  translate: (key: string, options?: Record<string, unknown>) => string;
}

// The bucket arrives as a server-computed UTC calendar key (YYYY-MM-DD for day, the UTC-Monday
// YYYY-MM-DD for week). It is rendered VERBATIM — never via new Date(bucket).toLocaleDateString,
// which would shift the label by one day in negative-offset locales (REF-08 §3.1 "UTC everywhere").
// Exported so the no-timezone-shift contract can be unit-tested directly.
export const bucketTickFormatter = (bucket: string): string => bucket;

export const InvestigationsOverTimeChart: React.FC<InvestigationsOverTimeChartProps> = ({
  series,
  granularity,
  isMobile,
  translate,
}) => {
  const seriesName = translate('aiTroubleshooting.analytics.charts.investigationsOverTime', {
    _: 'Investigations over time',
  });

  return (
    <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#1A1A1A',
          mb: 2,
          fontSize: { xs: '1rem', sm: '1.25rem' },
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
        }}
      >
        {seriesName}
        {granularity === 'week'
          ? ` (${translate('aiTroubleshooting.analytics.granularity.week', { _: 'Weekly' })})`
          : ''}
      </Typography>

      {series.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {translate('aiTroubleshooting.analytics.empty', { _: 'No data for this period.' })}
          </Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
          <LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
            <XAxis
              dataKey="bucket"
              tickFormatter={bucketTickFormatter}
              tick={{ fontSize: isMobile ? 10 : 12 }}
              stroke="#606060"
              interval={isMobile ? 'preserveStartEnd' : 0}
              minTickGap={isMobile ? 24 : 12}
            />
            <YAxis
              tick={{ fontSize: isMobile ? 10 : 12 }}
              stroke="#606060"
              tickFormatter={(value) => formatNumber(value, 0)}
              allowDecimals={false}
              width={isMobile ? 42 : 54}
            />
            <Tooltip
              formatter={(value: number) => formatNumber(value, 0)}
              labelFormatter={bucketTickFormatter}
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E0E0E0',
                borderRadius: 8,
              }}
            />
            <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#009A75"
              strokeWidth={isMobile ? 2 : 3}
              dot={{ r: isMobile ? 3 : 4, fill: '#009A75' }}
              name={seriesName}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default InvestigationsOverTimeChart;
